import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnalyticsBrowser, ID, UserTraits } from '@segment/analytics-next';

import { resetBrazeUser } from 'app/shared/utils/braze';
import { User } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';

import { useAnonymousId } from './useAnonymousId';
import { useBrazeConsentCleanup } from './useBrazeConsentCleanup';
import { useFireSegmentIdentify } from './useFireSegmentIdentify';
import { useSegmentIdentify } from './useSegmentIdentify';
import { useSegmentPage } from './useSegmentPage';
import { useSegmentReset } from './useSegmentReset';
import { useTrackAnalyticsEvent } from './useTrackAnalyticsEvent';

export interface AnalyticsContextValues {
  trackAnalyticsEvent: (
    eventDescription: string,
    options: { [key: string]: any },
    trackOnce?: boolean,
    eventIdentifier?: string
  ) => void;
  segmentIdentify: (
    user?: User | null | undefined,
    identifyTraits?: UserTraits
  ) => void;
  segmentPage: (title: string) => void;
  resetAnalyticsUser: () => void;
  anonymousId: ID;
  segmentInitialized: boolean;
  segmentFailedToInitialize: boolean;
}

export const SEGMENT_WRITE_KEY = process.env.SEGMENT_WRITE_KEY as string;
// We can use the same CDN url for all environments, the write key will dictate
// any environment specific info to pull down when used by AnalyticsBrowser.
export const SEGMENT_CDN_URL =
  process.env.SEGMENT_CDN_URL || 'https://analytics.sofarsounds.com';

export const AnalyticsContext = createContext<AnalyticsContextValues | null>(
  null
);

const getCookiebot = () =>
  typeof window !== 'undefined' ? (window as any).Cookiebot : undefined;
const hasStatisticsConsent = () => !!getCookiebot()?.consent?.statistics;

type SegmentInstance = ReturnType<typeof AnalyticsBrowser.load>;

const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const [analytics, setAnalytics] = useState<SegmentInstance | null>(null);
  const [segmentLoadedSuccessfully, setSegmentLoadedSuccessfully] =
    useState(false);
  const [segmentFailedToInitialize, setSegmentFailedToInitialize] =
    useState(false);
  const [eventsCalled, setEventsCalled] = useState<string[]>([]);
  const { user, loggedIn } = useContext(AuthContext);

  const baseTrackAnalyticsEvent = useTrackAnalyticsEvent(
    analytics ?? ({} as any),
    eventsCalled,
    setEventsCalled
  );

  const trackAnalyticsEventWithUser = useMemo(() => {
    return (
      eventDescription: string,
      properties: { [key: string]: any },
      trackOnce?: boolean,
      eventIdentifier?: string
    ) => {
      const email = loggedIn ? user?.email?.toLowerCase().trim() : undefined;
      const brazeExternalId = loggedIn ? user?.brazeExternalId : undefined;

      const enrichedProperties = {
        ...(email && { email }),
        ...(brazeExternalId && { braze_external_id: brazeExternalId }),
        ...properties,
      };

      return baseTrackAnalyticsEvent(
        eventDescription,
        enrichedProperties,
        trackOnce,
        eventIdentifier
      );
    };
  }, [baseTrackAnalyticsEvent, loggedIn, user?.email, user?.brazeExternalId]);

  const persistenceRef = useRef<boolean | null>(null);
  const loadedOnceRef = useRef(false);

  const loadSegment = (enablePersistence: boolean) => {
    const inst = AnalyticsBrowser.load(
      { writeKey: SEGMENT_WRITE_KEY, cdnURL: SEGMENT_CDN_URL },
      {
        cookie: {
          maxage: 90, // days
          sameSite: 'Lax',
        },
        disableClientPersistence: !enablePersistence,
      }
    );
    inst.ready(() => setSegmentLoadedSuccessfully(true));
    inst.catch(() => setSegmentFailedToInitialize(true));
    setAnalytics(inst);
    persistenceRef.current = enablePersistence;
    loadedOnceRef.current = true;
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const cookiebotObject = getCookiebot();
    const doInitialLoad = () => {
      loadSegment(hasStatisticsConsent());
    };

    if (cookiebotObject?.consent) {
      doInitialLoad();
      return;
    }

    const onConsentReady = () => {
      if (!loadedOnceRef.current) {
        doInitialLoad();
      }
    };
    window.addEventListener('CookiebotOnConsentReady', onConsentReady);

    const timeout = window.setTimeout(() => {
      if (!loadedOnceRef.current) {
        loadSegment(false);
      }
    }, 2000);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', onConsentReady);
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const reinitIfChanged = (
      desiredPersistence: boolean,
      { resetAnalytics }: { resetAnalytics: boolean }
    ) => {
      if (persistenceRef.current === desiredPersistence) {
        return;
      }

      if (resetAnalytics) {
        analytics?.reset?.();
      }
      setSegmentLoadedSuccessfully(false);
      setSegmentFailedToInitialize(false);
      loadSegment(desiredPersistence);
    };

    const onAccept = () => {
      if (hasStatisticsConsent()) {
        reinitIfChanged(true, { resetAnalytics: false });
      }
    };

    const onDecline = () => {
      reinitIfChanged(false, { resetAnalytics: true });
    };

    window.addEventListener('CookiebotOnAccept', onAccept);
    window.addEventListener('CookiebotOnDecline', onDecline);

    return () => {
      window.removeEventListener('CookiebotOnAccept', onAccept);
      window.removeEventListener('CookiebotOnDecline', onDecline);
    };
  }, [analytics]);

  const segmentIdentify = useSegmentIdentify(analytics ?? ({} as any));
  const segmentPage = useSegmentPage(analytics ?? ({} as any), setEventsCalled);
  const segmentReset = useSegmentReset(analytics ?? ({} as any));
  const anonymousId = useAnonymousId(analytics ?? ({} as any));

  const segmentInitialized =
    segmentLoadedSuccessfully || !!(analytics as any)?.track;

  useFireSegmentIdentify(segmentIdentify, segmentReset);
  useBrazeConsentCleanup();

  const resetAnalyticsUser = useCallback(() => {
    segmentReset();
    resetBrazeUser();
  }, [segmentReset]);

  const contextValue = useMemo(
    () => ({
      trackAnalyticsEvent: analytics ? trackAnalyticsEventWithUser : () => {},
      segmentIdentify: analytics ? segmentIdentify : () => {},
      segmentPage: analytics ? segmentPage : () => {},
      resetAnalyticsUser,
      anonymousId,
      segmentInitialized: !!analytics && segmentInitialized,
      segmentFailedToInitialize,
    }),
    [
      analytics,
      trackAnalyticsEventWithUser,
      segmentIdentify,
      segmentPage,
      resetAnalyticsUser,
      anonymousId,
      segmentInitialized,
      segmentFailedToInitialize,
    ]
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
